import React, { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Skeleton } from 'primereact/skeleton';

import { useWizardContext } from '../../../../queries/cafeWizard';

/**
 * Step: which screens get this campaign — device ASSIGNMENT, not signal
 * ROUTING. Those are two different things that used to be conflated here:
 * this step used to gate its own picker on `hasPtnHardware` (PTN routing
 * hardware — control_box/encoder/decoder), skipping straight to an
 * auto-assign-everything confirm line when a venue had none. But
 * `CafeWizardService::build()` already accepts any device subset — it has
 * no PTN dependency at all — so that gate did nothing except hide a
 * capability a non-PTN, multi-device café (a two-floor venue, say) had
 * every reason to want: different content on different screens. Routing a
 * live signal ACROSS screens (Match Mode's HDMI-in/receiver picture) is the
 * thing that genuinely needs PTN hardware — that's MatchWizard.jsx's own,
 * entirely separate receiver picker, untouched by this fix.
 *
 * The picker now always shows, for every venue. The good default survives:
 * every screen starts pre-selected (the common case — a café usually wants
 * the same show everywhere — costs no extra step), and narrowing to fewer
 * screens is something the owner has to actively choose, with a line
 * explaining what that choice means so the other screens' unchanged state
 * doesn't read as a bug.
 *
 * Driven by GET /cafe/wizard/context ({ screens[] }) — `hasPtnHardware` is
 * still returned by that endpoint (it's meaningful elsewhere) but no longer
 * read here.
 *
 * Props: value:string[], onChange:(ids)=>void
 */
export default function ScreensPickStep({ value = [], onChange }) {
  const { t, i18n } = useTranslation();
  const lng = i18n.language?.split('-')[0] || 'en';
  const fmtNumber = (n) => { try { return new Intl.NumberFormat(lng === 'ar' ? 'ar-EG' : 'en-US').format(n); } catch { return String(n); } };
  const { data: ctx, isLoading } = useWizardContext(true);

  const screens = useMemo(() => (Array.isArray(ctx?.screens) ? ctx.screens : []), [ctx]);

  // Seed once the context arrives: ALL screens pre-selected, every venue,
  // regardless of PTN hardware — narrowing is the owner's active choice,
  // never the default.
  const seeded = useRef(false);
  useEffect(() => {
    if (seeded.current || isLoading) return;
    if (screens.length) {
      seeded.current = true;
      onChange(screens.map((s) => s.id));
    }
  }, [isLoading, screens, onChange]);

  if (isLoading) {
    return (
      <div>
        <div className="fc-p-t">{t('cafe_wizard.which_screens')}</div>
        <div className="d-flex flex-column" style={{ gap: 9 }}>
          <Skeleton height="58px" borderRadius="11px" />
          <Skeleton height="58px" borderRadius="11px" />
        </div>
      </div>
    );
  }

  if (screens.length === 0) {
    return (
      <div>
        <div className="fc-p-t">{t('cafe_wizard.which_screens')}</div>
        <div className="fc-error">{t('cafe_wizard.no_screens')}</div>
      </div>
    );
  }

  // "All screens" always SETS every id (not a toggle); clicking one screen
  // while "all" is active narrows to just that one, otherwise it's a
  // normal add/remove toggle.
  const allSelected = screens.length > 0 && value.length === screens.length;
  const toggleAll = () => onChange(screens.map((s) => s.id));
  const toggleOne = (id) => {
    if (allSelected) { onChange([id]); return; }
    onChange(value.includes(id) ? value.filter((x) => x !== id) : [...value, id]);
  };

  return (
    <div>
      <div className="fc-p-t">{t('cafe_wizard.which_screens')}</div>
      <button type="button" className="fc-row" aria-pressed={allSelected} onClick={toggleAll}>
        <span className="ic">📺</span>
        <span className="b">
          <span className="t">{t('cafe_wizard.all_screens')}</span><br />
          <span className="s">{t('cafe_wizard.n_devices_total', { count: fmtNumber(screens.length) })}</span>
        </span>
      </button>
      {screens.map((sc) => {
        const on = value.includes(sc.id) && !allSelected;
        return (
          <button key={sc.id} type="button" className="fc-row" aria-pressed={on} onClick={() => toggleOne(sc.id)}>
            <span className="ic">▫️</span>
            <span className="b">
              <span className="t">{sc.name || sc.serial_name}</span><br />
              <span className="s">{sc.serial_name}</span>
            </span>
          </button>
        );
      })}
      {!value.length && <div className="fc-warn">{t('cafe_wizard.no_screens_selected_warn')}</div>}
      {/* ⚠️ SAY WHAT NARROWING MEANS, per the standing "disabled/changed
          control must say why" rule — without this, picking one screen
          out of three looks identical to picking all three except that
          the other two silently didn't change, which reads as a bug. */}
      {value.length > 0 && !allSelected && (
        <div className="fc-guide">{t('cafe_wizard.screens_narrowed_hint')}</div>
      )}
    </div>
  );
}
